import {
  PROPOSAL_BUNDLE_CATEGORY,
  type ForeignItinerary,
  type HotelProposalBundle,
  type HotelProposalItem,
  type PreferredTransportType,
  type ProposalBundle,
  type ProposalBundleCategory,
  type ProposalBundleRoute,
  type ProposalBundleState,
  type ProposalItemHotel,
  type ProposalItemTransport,
  type TicketCategory,
  type TransportProposalBundle,
  type TransportProposalItem
} from '../types/proposalBundles';
import type { FeedbackType } from '../types/messages';

type ProposalItemTransportResponse = {
  departure_time: string | null;
  arrival_time: string | null;
  departure_date: string | null;
  arrival_date: string | null;
  flight_name: string | null;
  is_connection_flight: boolean;
  connection_summary: string | null;
  seat_class: string | null;
  ticket_type: string | null;
  foreign_itinerary: ForeignItinerary | null;
  ticket_category: TicketCategory | null;
  is_air: boolean | null;
  is_shinkansen: boolean | null;
  segment_route_label: string | null;
  airline_display: string | null;
  round_trip_duration_label: string | null;
  leg_duration_summary: string | null;
  round_trip_flight_id: string | null;
  foreign_round_trip_display_key: string;
};

type ProposalItemHotelSegmentResponse = {
  index: number | null;
  checkin: string | null;
  checkout: string | null;
};

type ProposalItemHotelResponse = {
  stay_days: number | null;
  breakfast_exist: boolean | null;
  smoke: boolean | null;
  segment: ProposalItemHotelSegmentResponse;
};

type ProposalItemResponse = {
  id: number;
  title: string | null;
  subtitle: string | null;
  price: number | null;
  price_with_tax: number | null;
  price_text: string | null;
  ai_highlight_tags: string[];
  selected: boolean;
  transport: ProposalItemTransportResponse | null;
  hotel: ProposalItemHotelResponse | null;
};

type ProposalBundleRouteResponse = {
  origin: string | null;
  destination: string | null;
  departure_date: string | null;
  return_date: string | null;
  stay_days: number | null;
  preferred_transport_type: PreferredTransportType | null;
};

type ProposalBundlePartitionResponse = {
  partitioned: boolean | null;
  segment_count: number | null;
  all_segments_selected?: boolean | null;
};

type ProposalBundleResponseBase = {
  id: number;
  state: ProposalBundleState;
  route: ProposalBundleRouteResponse;
  items: ProposalItemResponse[];
  feedback?: FeedbackType | null;
  feedback_comment?: string | null;
  feedback_comment_submitted?: boolean;
};

type HotelProposalBundleResponse = ProposalBundleResponseBase & {
  category: typeof PROPOSAL_BUNDLE_CATEGORY.HOTEL;
  partition: ProposalBundlePartitionResponse;
};

type TransportProposalBundleResponse = ProposalBundleResponseBase & {
  category: Exclude<ProposalBundleCategory, typeof PROPOSAL_BUNDLE_CATEGORY.HOTEL>;
  has_foreign_flight_round_trip: boolean | null;
  has_foreign_flight: boolean | null;
};

type ProposalBundleResponse = HotelProposalBundleResponse | TransportProposalBundleResponse;

export type FetchProposalBundlesResponse = {
  bundles: ProposalBundleResponse[];
};

const mapProposalItemTransport = (transport: ProposalItemTransportResponse): ProposalItemTransport => ({
  departureTime: transport.departure_time,
  arrivalTime: transport.arrival_time,
  departureDate: transport.departure_date ?? null,
  arrivalDate: transport.arrival_date ?? null,
  flightName: transport.flight_name,
  isConnectionFlight: transport.is_connection_flight,
  connectionSummary: transport.connection_summary,
  seatClass: transport.seat_class,
  ticketType: transport.ticket_type,
  foreignItinerary: transport.foreign_itinerary ?? null,
  ticketCategory: transport.ticket_category,
  isAir: transport.is_air,
  isShinkansen: transport.is_shinkansen,
  segmentRouteLabel: transport.segment_route_label ?? null,
  airlineDisplay: transport.airline_display ?? null,
  roundTripDurationLabel: transport.round_trip_duration_label ?? null,
  legDurationSummary: transport.leg_duration_summary ?? null,
  roundTripFlightId: transport.round_trip_flight_id != null ? String(transport.round_trip_flight_id) : null,
  foreignRoundTripDisplayKey: transport.foreign_round_trip_display_key
});

const mapProposalItemHotel = (hotel: ProposalItemHotelResponse): ProposalItemHotel => ({
  stayDays: hotel.stay_days,
  breakfastExist: hotel.breakfast_exist,
  smoke: hotel.smoke,
  segment: {
    index: hotel.segment.index ?? null,
    checkin: hotel.segment.checkin ?? null,
    checkout: hotel.segment.checkout ?? null
  }
});

const mapTransportProposalItemResponse = (item: ProposalItemResponse): TransportProposalItem => {
  if (item.transport == null) {
    throw new Error(`Transport proposal item ${item.id} must include transport`);
  }

  return {
    id: item.id,
    title: item.title,
    subtitle: item.subtitle,
    price: item.price,
    priceWithTax: item.price_with_tax,
    priceText: item.price_text,
    aiHighlightTags: item.ai_highlight_tags,
    selected: item.selected,
    transport: mapProposalItemTransport(item.transport),
    hotel: null
  };
};

const mapHotelProposalItemResponse = (item: ProposalItemResponse): HotelProposalItem => {
  if (item.hotel == null) {
    throw new Error(`Hotel proposal item ${item.id} must include hotel`);
  }

  return {
    id: item.id,
    title: item.title,
    subtitle: item.subtitle,
    price: item.price,
    priceWithTax: item.price_with_tax,
    priceText: item.price_text,
    aiHighlightTags: item.ai_highlight_tags,
    selected: item.selected,
    transport: null,
    hotel: mapProposalItemHotel(item.hotel)
  };
};

const mapProposalBundleRoute = (route: ProposalBundleRouteResponse): ProposalBundleRoute => ({
  origin: route.origin,
  destination: route.destination,
  departureDate: route.departure_date,
  returnDate: route.return_date,
  stayDays: route.stay_days,
  preferredTransportType: route.preferred_transport_type
});

const mapHotelProposalBundleResponse = (bundle: HotelProposalBundleResponse): HotelProposalBundle => ({
  id: bundle.id,
  category: PROPOSAL_BUNDLE_CATEGORY.HOTEL,
  state: bundle.state,
  route: mapProposalBundleRoute(bundle.route),
  partition: {
    partitioned: bundle.partition.partitioned === true,
    segmentCount: bundle.partition.segment_count ?? null,
    allSegmentsSelected: bundle.partition.all_segments_selected ?? null
  },
  items: bundle.items.map(mapHotelProposalItemResponse),
  feedback: bundle.feedback ?? null,
  feedbackCommentSubmitted: bundle.feedback_comment_submitted ?? false
});

const mapTransportProposalBundleResponse = (bundle: TransportProposalBundleResponse): TransportProposalBundle => ({
  id: bundle.id,
  category: bundle.category,
  state: bundle.state,
  route: mapProposalBundleRoute(bundle.route),
  hasForeignFlightRoundTrip: bundle.has_foreign_flight_round_trip ?? null,
  hasForeignFlight: bundle.has_foreign_flight ?? null,
  items: bundle.items.map(mapTransportProposalItemResponse),
  feedback: bundle.feedback ?? null,
  feedbackCommentSubmitted: bundle.feedback_comment_submitted ?? false
});

export const mapProposalBundleResponse = (bundle: ProposalBundleResponse): ProposalBundle => {
  if (bundle.category === PROPOSAL_BUNDLE_CATEGORY.HOTEL) {
    return mapHotelProposalBundleResponse(bundle);
  }

  return mapTransportProposalBundleResponse(bundle);
};

export const mapProposalBundlesResponse = (response: FetchProposalBundlesResponse): ProposalBundle[] =>
  response.bundles.map(mapProposalBundleResponse);
